/**
 * StampBook Component
 *
 * Full-screen modal showing all trial stamps in an elegant book format.
 * Similar to BadgeBook but for Elite 4 trial completions.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { X, Award, Trophy, MapPin, Flame } from 'lucide-react';
import { ELITE_4_TRIALS, TRIAL_REGIONS } from '../data/elite4Trials';

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 }
};

const stampVariants = {
  hidden: { opacity: 0, scale: 0.8, rotate: -10 },
  visible: { opacity: 1, scale: 1, rotate: 0 }
};

// Individual stamp card with full details
const StampCard = ({ region, regionKey, stampData, delay }) => {
  const owned = !!stampData;
  const typeColor = region.color || '#6B7280';

  return (
    <motion.div
      variants={stampVariants}
      initial="hidden"
      animate="visible"
      transition={{ delay: delay * 0.1, type: 'spring', stiffness: 200 }}
      className={`relative rounded-2xl p-4 transition-all ${
        owned
          ? 'bg-gradient-to-br from-amber-50 via-white to-amber-50 border-2 border-amber-300 shadow-lg'
          : 'bg-gray-100 border-2 border-gray-300'
      }`}
    >
      {/* Stamp visual */}
      <div className="flex justify-center mb-3">
        <div className="relative">
          {/* Stamp circle */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center border-4 ${
              owned ? 'border-amber-400' : 'border-gray-400'
            }`}
            style={{
              background: owned
                ? `linear-gradient(135deg, ${typeColor}40, ${typeColor}80)`
                : 'linear-gradient(135deg, #9CA3AF, #6B7280)',
              boxShadow: owned
                ? `0 4px 15px ${typeColor}40, inset 0 2px 6px rgba(255,255,255,0.4)`
                : '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            {owned ? (
              <Award size={36} className="text-white drop-shadow-lg" />
            ) : (
              <Flame size={32} className="text-gray-400" />
            )}
          </div>

          {/* Completion indicator */}
          {owned && (
            <div className="absolute -top-1 -right-1 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-md">
              <Trophy size={14} className="text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Region name */}
      <h4 className={`text-lg font-bold text-center mb-1 ${owned ? 'text-pocket-text' : 'text-gray-400'}`}>
        {region.name}
      </h4>

      {/* Region info */}
      <div className="flex items-center justify-center gap-1 mb-2">
        <MapPin size={12} className={owned ? 'text-pocket-text-light' : 'text-gray-300'} />
        <span className={`text-xs ${owned ? 'text-pocket-text-light' : 'text-gray-400'}`}>
          {region.region}
        </span>
      </div>

      {/* Generation badge */}
      <div className="flex justify-center mb-2">
        <span
          className={`text-xs px-3 py-1 rounded-full font-semibold ${
            owned ? 'bg-gray-100 text-pocket-text' : 'bg-gray-200 text-gray-400'
          }`}
        >
          Generation {region.generation}
        </span>
      </div>

      {/* Elite 4 members defeated */}
      {owned && (
        <div className="flex justify-center gap-1 mt-2">
          {region.members.map((member, idx) => (
            <div
              key={idx}
              className="w-6 h-6 rounded-full overflow-hidden border-2 border-green-400 relative"
              title={member.name}
            >
              {member.sprite ? (
                <>
                  <img
                    src={member.sprite}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                    style={{ transform: 'scale(1.8) translateY(2px)' }}
                  />
                  <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold drop-shadow-lg">✓</span>
                  </div>
                </>
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: region.color || '#9CA3AF' }}
                >
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Completion date */}
      {owned && stampData?.completed_at && (
        <p className="text-center text-[10px] text-pocket-text-light mt-2">
          Completed {new Date(stampData.completed_at).toLocaleDateString()}
        </p>
      )}

      {/* Not collected */}
      {!owned && (
        <div className="text-center mt-2">
          <span className="text-xs text-gray-400 font-medium">
            Not Completed
          </span>
        </div>
      )}
    </motion.div>
  );
};

const StampBook = ({ stamps = {}, onClose }) => {
  const totalStamps = TRIAL_REGIONS.length;
  const collectedCount = Object.keys(stamps).length;

  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Award size={24} className="text-amber-300" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Stamp Book</h2>
                <p className="text-white/80 text-sm">
                  {collectedCount} / {totalStamps} Collected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-amber-300 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(collectedCount / totalStamps) * 100}%` }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </div>
        </div>

        {/* Stamp grid - scrollable area */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {TRIAL_REGIONS.map((regionKey, idx) => {
              const region = ELITE_4_TRIALS[regionKey];
              return (
                <StampCard
                  key={regionKey}
                  region={region}
                  regionKey={regionKey}
                  stampData={stamps[regionKey]}
                  delay={idx}
                />
              );
            })}
          </div>

          {/* Empty state */}
          {collectedCount === 0 && (
            <div className="text-center py-6 mt-4">
              <Flame size={48} className="mx-auto mb-3 text-red-300" />
              <p className="text-pocket-text-light text-sm">
                Complete Elite 4 Trials to collect stamps!
              </p>
              <p className="text-pocket-text-light text-xs mt-1">
                Each region has 4 Elite 4 members to defeat.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gradient-to-r from-red-100 to-orange-100 border-t border-red-200 flex-shrink-0">
          <div className="flex items-center justify-between text-sm flex-wrap gap-2">
            <div className="flex items-center gap-2 text-red-700">
              <Trophy size={14} className="text-amber-500" />
              <span className="text-xs sm:text-sm">Defeat all 4 Elite 4 members in a region to earn a stamp!</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StampBook;
